import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';

import { AppConfigService } from './app-config.service';
import { FeatureFlagService } from './feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../shared/interfaces/feature-flag.interface';
import { UserAclService } from './user-acl.service';
import { RequiredSecurities } from '../../../shared/constants/required-securities';
import { TenantApi } from './rest/tenant.api';
import { ITenantScimConfig } from '../../../shared/interfaces/tenant.interface';
import { AngularUtility } from '../utility/angular.utility';

@Injectable({
  providedIn: 'root',
})
export class ScimConfigService {
  private readonly scimConfigChanged$: Subject<void> = new Subject<void>();
  private readonly isScimEnabledStream$: Observable<boolean> = this.buildIsScimEnabled$();

  constructor(
    private appConfigService: AppConfigService,
    private featureFlagService: FeatureFlagService,
    private userAclService: UserAclService,
    private tenantApi: TenantApi,
  ) {}

  public getScimConfig(): Observable<ITenantScimConfig> {
    return this.tenantApi.getScimConfig();
  }

  public setScimConfig(config: ITenantScimConfig): Observable<ITenantScimConfig> {
    return this.tenantApi.setScimConfig(config).pipe(tap(() => this.scimConfigChanged$.next()));
  }

  public isScimEnabled$(): Observable<boolean> {
    return this.isScimEnabledStream$;
  }

  private buildIsScimEnabled$(): Observable<boolean> {
    if (!this.appConfigService.isTenantEnabled()) {
      return of(false);
    }

    const scimFeatureFlagEnabled$: Observable<boolean> = this.featureFlagService
      .variation$(FeatureFlagCollection.scimFunctionality, false)
      .pipe(map(Boolean));

    const canReadScimConfig$: Observable<boolean> = this.userAclService.checkSecurityAccessForTenant(
      RequiredSecurities.TI_SCIM_Config_Read,
    );

    const scimConfigChanged$: Observable<void> = this.scimConfigChanged$.pipe(startWith(undefined));

    return combineLatest([scimFeatureFlagEnabled$, canReadScimConfig$, scimConfigChanged$]).pipe(
      switchMap(([scimFeatureFlagEnabled, canReadScimConfig]: [boolean, boolean, void]) =>
        scimFeatureFlagEnabled && canReadScimConfig ? this.isConfiguredForTenant$() : of(false),
      ),
      AngularUtility.shareRef(),
    );
  }

  private isConfiguredForTenant$(): Observable<boolean> {
    return this.getScimConfig().pipe(
      map((config: ITenantScimConfig): boolean => config.enabled && config.ssoConfigured),
      catchError(() => of(false)),
    );
  }
}
